/**
 * Where the support pages live, and the page a new ticket was opened from
 * (`SUP-WEB01`). No imports: the site bar's account menu, on every public
 * page, links here, and must not bring the support pages' code with it.
 */

/** `docs/07` "Support": `context.path` is at most 300 characters. */
export const SUPPORT_CONTEXT_PATH_MAX = 300;

/**
 * A payment link's page carries its token in the path (`/[locale]/payment-link/<token>`);
 * a ticket keeps the page without it (`SUP-AI03`: the help launcher shows there).
 */
const PAYMENT_LINK_PAGE = /^(\/[a-z]{2}\/payment-link)\/[^/]+(?:\/.*)?$/;

/**
 * The page a ticket was opened from, as `context.path`: a path on this site,
 * never a query string or a fragment (the server drops both too), within the
 * route's 300 characters. Anything else is no context at all.
 */
export function contextPathFrom(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  const path = (raw.split(/[?#]/)[0] ?? "").replace(PAYMENT_LINK_PAGE, "$1");
  if (!path.startsWith("/") || path.startsWith("//") || path.includes("\\")) return undefined;
  if (path.length > SUPPORT_CONTEXT_PATH_MAX) return undefined;
  return path;
}

/** The person's tickets; `from` is the page they came from, carried to a new ticket. */
export function supportHomeHref(locale: string, from?: string): string {
  const path = contextPathFrom(from);
  return `/${locale}/dashboard/support${path ? `?from=${encodeURIComponent(path)}` : ""}`;
}

export function supportTicketHref(locale: string, number: number): string {
  return `/${locale}/dashboard/support/${number}`;
}

/**
 * The new ticket form, filled from where it was opened: `from` becomes the
 * ticket's `context.path`; a closed ticket's "open a new one" also passes its
 * topic, subject and number (`follow`), which starts the message, so the team
 * can find the ticket it follows. The help chat passes its chat's id (`chat`,
 * `SUP-AI03`): the form reads the person's own chat by it, so their questions
 * never travel in the address.
 */
export function newTicketHref(
  locale: string,
  options: { from?: string; category?: string; subject?: string; follow?: number; chat?: string } = {},
): string {
  const params = new URLSearchParams();
  const path = contextPathFrom(options.from);
  if (path) params.set("from", path);
  if (options.category) params.set("category", options.category);
  if (options.subject) params.set("subject", options.subject);
  if (options.follow) params.set("follow", String(options.follow));
  if (options.chat && isChatId(options.chat)) params.set("chat", options.chat);
  const query = params.toString();
  return `/${locale}/dashboard/support/new${query ? `?${query}` : ""}`;
}

/** A chat's id as the API writes one: 24 hex characters. */
export function isChatId(raw: string | null | undefined): raw is string {
  return typeof raw === "string" && /^[a-f0-9]{24}$/i.test(raw);
}
